import {
  collectPendingRichHtmlAttachmentIds,
  collectRichHtmlAttachmentIds,
} from './richHtmlAttachmentRefs';

const UNSAFE_FILENAME_CHARS = /[<>:"/\\|?*\u0000-\u001f]/g;

function extensionFromMime(mimeType: string): string {
  const subtype = mimeType.split('/')[1]?.split(';')[0]?.trim().toLowerCase();
  if (subtype === 'jpeg') {
    return 'jpg';
  }
  if (subtype === 'svg+xml') {
    return 'svg';
  }
  if (subtype && /^[a-z0-9]+$/.test(subtype)) {
    return subtype;
  }
  return 'png';
}

function formatDateStamp(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function sanitizeFieldLabel(fieldLabel: string): string {
  const cleaned = fieldLabel
    .replace(UNSAFE_FILENAME_CHARS, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  return cleaned || 'Image';
}

export function nextInlineImageSequence(
  fieldHtml: string,
  extraCount = 0,
): number {
  return (
    collectRichHtmlAttachmentIds(fieldHtml).length +
    collectPendingRichHtmlAttachmentIds(fieldHtml).length +
    extraCount +
    1
  );
}

export function countUnreferencedStagedInlineImages(
  fieldHtml: string,
  requestSection: string,
  staged: ReadonlyArray<{
    clientId: string;
    requestSection: string;
    requestId?: number | null;
    fieldLabel?: string;
  }>,
  match?: { requestId?: number | null; fieldLabel?: string },
): number {
  const pendingInHtml = new Set(collectPendingRichHtmlAttachmentIds(fieldHtml));
  return staged.filter(record => {
    if (record.requestSection !== requestSection) {
      return false;
    }
    if (
      match?.requestId != null &&
      record.requestId != null &&
      record.requestId !== match.requestId
    ) {
      return false;
    }
    if (
      match?.fieldLabel != null &&
      record.fieldLabel != null &&
      record.fieldLabel !== match.fieldLabel
    ) {
      return false;
    }
    return !pendingInHtml.has(record.clientId);
  }).length;
}

const inFlightByField = new Map<string, number>();
const uploadInFlightListeners = new Set<() => void>();

export type InlineImageUploadProgress = {
  fileName: string;
  progress: number;
  status: 'uploading' | 'completed' | 'error';
};

let inlineImageUploadProgress: InlineImageUploadProgress | null = null;
const uploadProgressListeners = new Set<() => void>();

function notifyUploadInFlightListeners(): void {
  uploadInFlightListeners.forEach(listener => listener());
}

function notifyUploadProgressListeners(): void {
  uploadProgressListeners.forEach(listener => listener());
}

export function getInlineImageUploadProgress(): InlineImageUploadProgress | null {
  return inlineImageUploadProgress;
}

export function setInlineImageUploadProgress(
  progress: InlineImageUploadProgress | null,
): void {
  inlineImageUploadProgress = progress;
  notifyUploadProgressListeners();
}

export function subscribeInlineImageUploadProgress(
  listener: () => void,
): () => void {
  uploadProgressListeners.add(listener);
  return () => {
    uploadProgressListeners.delete(listener);
  };
}

export function inlineImageFieldKey(
  requestSection: string,
  fieldLabel: string,
): string {
  return `${requestSection}\u0000${fieldLabel}`;
}

/** True while any TextArea field is uploading or staging an inline image. */
export function hasInlineImageUploadsInFlight(): boolean {
  for (const count of inFlightByField.values()) {
    if (count > 0) {
      return true;
    }
  }
  return false;
}

export function subscribeInlineImageUploadsInFlight(
  listener: () => void,
): () => void {
  uploadInFlightListeners.add(listener);
  return () => {
    uploadInFlightListeners.delete(listener);
  };
}

/** Returns how many attempts are already in flight for this field, then reserves a slot. */
export function beginInlineImageAttempt(fieldKey: string): number {
  const current = inFlightByField.get(fieldKey) ?? 0;
  inFlightByField.set(fieldKey, current + 1);
  notifyUploadInFlightListeners();
  return current;
}

export function endInlineImageAttempt(fieldKey: string): void {
  const current = inFlightByField.get(fieldKey) ?? 0;
  if (current <= 1) {
    inFlightByField.delete(fieldKey);
  } else {
    inFlightByField.set(fieldKey, current - 1);
  }
  notifyUploadInFlightListeners();
}

const IDLE_POLL_MS = 50;
const DEFAULT_IDLE_WAIT_MS = 15_000;

async function waitForEditorHtmlSync(): Promise<void> {
  await new Promise<void>(resolve => {
    setTimeout(resolve, 0);
  });
  await new Promise<void>(resolve => {
    setTimeout(resolve, 0);
  });
}

/** Block until staging/upload + editor insert have finished (or timeout). */
export async function waitForInlineImagePipelineIdle(
  timeoutMs = DEFAULT_IDLE_WAIT_MS,
): Promise<boolean> {
  const deadline = Date.now() + timeoutMs;
  while (hasInlineImageUploadsInFlight()) {
    if (Date.now() >= deadline) {
      return false;
    }
    await new Promise(resolve => setTimeout(resolve, IDLE_POLL_MS));
  }
  await waitForEditorHtmlSync();
  return !hasInlineImageUploadsInFlight();
}

/**
 * Readable name for a pasted/inserted image: field label + sequence + date.
 * Replaces the browser's raw clipboard filename.
 */
export function generateInlineAttachmentName(options: {
  fieldLabel: string;
  sequence: number;
  mimeType: string;
  date?: Date;
}): string {
  const dateStamp = formatDateStamp(options.date ?? new Date());
  const extension = extensionFromMime(options.mimeType);
  const suffix = ` ${options.sequence} ${dateStamp}.${extension}`;
  const maxLabelLength = Math.max(1, 255 - suffix.length);
  const label = sanitizeFieldLabel(options.fieldLabel).slice(0, maxLabelLength);
  return `${label}${suffix}`;
}
